import React from "react";
import { Box, Flex, Radio, RadioGroup, Text } from "@chakra-ui/react";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import PathPickPriceInput from "@/features/pathpick/components/ui/PathPickPriceInput";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { PathPickSalePriceRuleType } from "@/features/pathpick/features/basket/types";

// 방식이 바뀌어도 입력 폭이 그대로여야 좌우 라벨이 흔들리지 않는다.
const MARKUP_INPUT_WIDTH = "160px";

// 예상 공급가를 얼마나 올려 판매가로 쓸지 — 비율(%)이냐 고정 금액(원)이냐.
const MARKUP_TYPES = ["PERCENT", "AMOUNT"] as const;

const MARKUP_TYPE_LABEL_KEYS: Record<PathPickSalePriceRuleType, string> = {
  PERCENT: "path_pick:basket.buyer_info.selling_price.type_rate",
  AMOUNT: "path_pick:basket.buyer_info.selling_price.type_amount",
};

interface Props {
  markupType: PathPickSalePriceRuleType;
  // 비율과 금액은 단위가 달라 값을 따로 들고 있는다 —
  // 한 값을 공유하면 10%로 두고 금액으로 바꿨을 때 10원이 된다.
  markupRate?: number;
  markupAmount?: number;
  isSalePriceSync: boolean;
  onChangeMarkupType: (markupType: PathPickSalePriceRuleType) => void;
  onChangeMarkupRate: (markupRate?: number) => void;
  onChangeMarkupAmount: (markupAmount?: number) => void;
  onChangeSalePriceSync: (isSalePriceSync: boolean) => void;
}

// 상품 등록 시 판매가 설정 — 판매자 기본 정보 설정 모달의 "상품 등록 시 판매가 설정" 섹션 본문.
// (카드 헤더는 섹션 셸이 그린다)
function PathPickBasketSellingPriceField({
  markupType,
  markupRate,
  markupAmount,
  isSalePriceSync,
  onChangeMarkupType,
  onChangeMarkupRate,
  onChangeMarkupAmount,
  onChangeSalePriceSync,
}: Props) {
  const { t } = useI18nHelper();

  const isRate = markupType === "PERCENT";

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      <RadioGroup
        value={markupType}
        onChange={(next) =>
          onChangeMarkupType(next as PathPickSalePriceRuleType)
        }
      >
        <Flex gap={"24px"} alignItems={"center"}>
          {MARKUP_TYPES.map((type) => (
            <Radio
              key={type}
              value={type}
              size={"sm"}
              borderColor={"#D4D4D1"}
              _checked={{
                background: "#1272EF",
                borderColor: "#1272EF",
                color: "#FFFFFF",
              }}
              _focusVisible={{ boxShadow: "none" }}
            >
              <Text textStyle={"body-sm-medium"} color={"#1D1D1B"}>
                {t(MARKUP_TYPE_LABEL_KEYS[type])}
              </Text>
            </Radio>
          ))}
        </Flex>
      </RadioGroup>

      {/* 예상 공급가로부터 [ 10 % ] 인상 후 1,000원 단위 올림
          좁은 폭에서는 한 줄에 들어가지 않아 문구 단위로 줄바꿈을 허용한다. */}
      <Flex gap={"12px"} alignItems={"center"} flexWrap={"wrap"}>
        <Text
          textStyle={"body-sm-medium"}
          color={"#1D1D1B"}
          whiteSpace={"nowrap"}
        >
          {t("path_pick:basket.buyer_info.selling_price.from_supply_price")}
        </Text>
        {/* PathPickPriceInput의 InputGroup은 폭이 100%라 바깥에서 폭을 잡아준다. */}
        <Box width={MARKUP_INPUT_WIDTH} flexShrink={0}>
          {isRate ? (
            <PathPickPriceInput
              value={markupRate}
              onChange={onChangeMarkupRate}
              suffix={t("path_pick:basket.buyer_info.selling_price.percent")}
            />
          ) : (
            <PathPickPriceInput
              value={markupAmount}
              onChange={onChangeMarkupAmount}
              suffix={t("path_pick:basket.buyer_info.won")}
            />
          )}
        </Box>
        <Text
          textStyle={"body-sm-medium"}
          color={"#1D1D1B"}
          whiteSpace={"nowrap"}
        >
          {t("path_pick:basket.buyer_info.selling_price.markup_suffix")}
        </Text>
      </Flex>

      <Flex flexDirection={"column"} gap={"4px"}>
        <AppCheckBox
          size={"small"}
          isChecked={isSalePriceSync}
          name={t(
            "path_pick:basket.buyer_info.selling_price.auto_update_label"
          )}
          labelFontSize={"13px"}
          onChange={onChangeSalePriceSync}
        />
        {/* 체크박스(16px) + gap(8px) 만큼 들여써 라벨에 딸린 설명으로 읽히게 한다. */}
        <Text
          textStyle={"body-sm-medium"}
          color={"#8F8F8C"}
          paddingLeft={"24px"}
        >
          {t(
            "path_pick:basket.buyer_info.selling_price.auto_update_description"
          )}
        </Text>
      </Flex>
    </Flex>
  );
}

export default PathPickBasketSellingPriceField;
